/**
 * The supported countries and the languages of the app, and the storage keys the entry page of the native
 * app (src/native-start/start.ts) and the Angular app share. No Angular imports here: the entry page bundles
 * this file with esbuild.
 */

/** Keys in Capacitor Preferences (native storage – survives app updates and restarts; in a browser: localStorage). */
export const CHOSEN_MARKET_KEY = 'market';          // the country of this device: 'de' or 'ie' (read by market-resolver.ts)
export const CHOSEN_LANGUAGE_KEY = 'language';      // folder of the app language: 'de' or 'en' (read by the entry page)

/** Keys in sessionStorage – they survive a reload and are emptied when the tab or the app process ends. */
export const COUNTRY_OF_THIS_SESSION_KEY = 'countryOfThisSession';   // the country a reload was made for (plan D41)
export const REGISTRATION_COUNTRY_KEY = 'registrationCountry';       // the country chosen on "Choose your country"
export const PATH_AFTER_RELOAD_KEY = 'pathAfterReload';              // the register page to open after that reload
export const LAUNCH_LINK_HANDLED_KEY = 'launchLinkHandled';          // getLaunchUrl() was already read (unchanged)

/**
 * sessionStorage throws when the browser blocks site data. These three helpers never throw –
 * a note that cannot be read is simply "not there".
 */
export function readSessionNote(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Returns false when the note could not be written. */
export function writeSessionNote(key: string, value: string): boolean {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeSessionNote(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // nothing to remove
  }
}

/** One entry of the drop-down "Choose your country". */
export interface SupportedCountry {
  /** The country code – the ID of its market, the value in the account register: 'de', 'ie'. */
  id: string;
  flag: string;
  countryName: { en: string; de: string };
}

/**
 * The countries a user can choose – the ONLY ones (plan D32). For the app and for the websites.
 * German-only release: only 'de'. Plan Part 7.1 adds Ireland.
 * Keep in step with the country list in firestore.rules and in functions/src/markets.ts.
 */
export const SUPPORTED_COUNTRIES: SupportedCountry[] = [
  { id: 'de', flag: '🇩🇪', countryName: { en: 'Germany · Austria · Switzerland', de: 'Deutschland · Österreich · Schweiz' } },
  // Part 7.1: { id: 'ie', flag: '🇮🇪', countryName: { en: 'Ireland', de: 'Irland' } },
];

export function isSupportedCountryId(countryId: string | null | undefined): boolean {
  return SUPPORTED_COUNTRIES.some(country => country.id === countryId);
}

/** The languages of the app – one build folder each (dist/native/<folder>, plan 6.2). */
export interface AppLanguage {
  folder: string;
  /** Name of the language, written in that language – for the language row of the screens. */
  name: string;
}

/** PR 3a: only German. PR 3b adds { folder: 'en', name: 'English' }. */
export const APP_LANGUAGES: AppLanguage[] = [
  { folder: 'de', name: 'Deutsch' },
  // PR 3b: { folder: 'en', name: 'English' },
];

/** Country detection for "Is this your country?" – first the time zone, it tells where the phone is used … */
export const MARKET_ID_BY_TIME_ZONE: Record<string, string> = {
  'Europe/Berlin': 'de', 'Europe/Busingen': 'de', 'Europe/Vienna': 'de', 'Europe/Zurich': 'de',
  'Europe/Vaduz': 'de', 'Europe/Luxembourg': 'de',
  // PR 3b: 'Europe/Dublin': 'ie',
};

/** … then the region part of the language setting ('en-IE' → 'IE'). */
export const MARKET_ID_BY_REGION: Record<string, string> = {
  DE: 'de', AT: 'de', CH: 'de', LI: 'de', LU: 'de',
  // PR 3b: IE: 'ie',
};

/**
 * The country that fits the device, or null. Used to pre-select the drop-down "Choose your country" and
 * as the country setup of an app that knows no country yet (market-resolver.ts).
 * The time zone is the best hint for the country the phone is used in. The language tag often has no region
 * or the "wrong" one: many Irish phones say 'en-GB', and iOS keeps the region setting apart from the language.
 */
export function detectMarketIdFromDevice(): string | null {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;   // e.g. 'Europe/Dublin'
  const marketIdFromTimeZone = MARKET_ID_BY_TIME_ZONE[timeZone];
  if (isSupportedCountryId(marketIdFromTimeZone)) {
    return marketIdFromTimeZone;
  }

  const languageTag = navigator.language || '';     // e.g. 'de-DE', 'en-IE', 'en'
  const languageTagParts = languageTag.split('-');
  if (languageTagParts.length < 2) {
    return null;                                    // no region in the tag → nothing detected
  }
  const region = languageTagParts[languageTagParts.length - 1].toUpperCase();
  const marketIdFromRegion = MARKET_ID_BY_REGION[region];
  if (isSupportedCountryId(marketIdFromRegion)) {
    return marketIdFromRegion;
  }
  return null;
}